/**
 * CollapsibleSection — the card-with-collapsible-body shell used by the
 * Action Center's stacked sections (Needs Assignment, Support requests) and
 * by the Needs Assignment card on the Red Dot workflow home.
 *
 * The header is always a button (the whole strip toggles), with three optional
 * slots: a count badge next to the title, a `meta` slot rendered inline in the
 * header regardless of state, and an `actions` slot (filter pills and the like)
 * that is hidden while collapsed — collapsed means "get this out of my way",
 * so controls for hidden content must go with it. `actions` sits inside the
 * header but OUTSIDE the toggle button: nesting interactive controls in a
 * button is invalid HTML and would swallow their clicks into the toggle.
 *
 * Open/closed state is remembered per `storageKey` in localStorage so an admin
 * who collapses a section doesn't have to re-collapse it on every visit.
 * `forceOpen` opens the section when it flips on, for cases where the content
 * must become visible — e.g. a deep link that opens a specific item inside it.
 * It seeds the state rather than pinning it, so the user can still collapse the
 * section afterwards.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { ChevronDown } from 'lucide-react';

const Card = styled.section`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`;

const Header = styled.div<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[5]}`};
  border-bottom: ${({ theme, $open }) =>
    $open ? `1px solid ${theme.colors.borders.light}` : 'none'};
  flex-wrap: wrap;
`;

/* The toggle covers the title/badge/meta run and stretches to fill the row, so
   clicking anywhere in the header's empty space toggles too. */
const Toggle = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  color: inherit;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 3px;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
`;

const Chevron = styled(ChevronDown)<{ $open: boolean }>`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: transform ${({ theme }) => theme.transitions.fast};
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.status.warning.bg};
  color: ${({ theme }) => theme.colors.status.warning.text};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-left: auto;
  flex-wrap: wrap;
`;

export interface CollapsibleSectionProps {
  title: string;
  /** Shown as a pill beside the title when > 0. */
  count?: number;
  /** Stable key for the remembered open/closed state (localStorage). */
  storageKey: string;
  /** Initial state the first time this user sees the section. */
  defaultOpen?: boolean;
  /** Opens the section when it flips true (deep links); the user may still collapse it after. */
  forceOpen?: boolean;
  /** Inline header content (e.g. a subtitle) — visible in both states. */
  meta?: ReactNode;
  /** Header-right controls; hidden while collapsed. */
  actions?: ReactNode;
  children: ReactNode;
}

const storageKeyFor = (key: string) => `section-open:${key}`;

function readStoredOpen(key: string, fallback: boolean): boolean {
  try {
    const raw = window.localStorage.getItem(storageKeyFor(key));
    return raw === null ? fallback : raw === '1';
  } catch {
    // Private-mode/blocked storage — fall back to the default rather than crashing.
    return fallback;
  }
}

export function CollapsibleSection({
  title,
  count,
  storageKey,
  defaultOpen = true,
  forceOpen = false,
  meta,
  actions,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(() => forceOpen || readStoredOpen(storageKey, defaultOpen));

  // Re-read when the key changes (a section reused for a different queue).
  useEffect(() => {
    setOpen(readStoredOpen(storageKey, defaultOpen));
    // defaultOpen only seeds the first read; re-running on its identity would
    // fight the user's stored choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // A deep link arriving while the section is collapsed must reveal it.
  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(storageKeyFor(storageKey), next ? '1' : '0');
      } catch {
        // Non-fatal: the section still toggles, it just won't be remembered.
      }
      return next;
    });
  }, [storageKey]);

  const bodyId = `collapsible-${storageKey.replace(/[^a-zA-Z0-9-]/g, '-')}`;

  return (
    <Card>
      <Header $open={open}>
        <Toggle type="button" onClick={toggle} aria-expanded={open} aria-controls={bodyId}>
          <Chevron $open={open} aria-hidden="true" />
          <Title>{title}</Title>
          {count !== undefined && count > 0 && <CountBadge>{count}</CountBadge>}
          {meta}
        </Toggle>
        {open && actions && <Actions>{actions}</Actions>}
      </Header>
      {open && <div id={bodyId}>{children}</div>}
    </Card>
  );
}

export default CollapsibleSection;
